import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, ShieldCheck, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { complianceEngineService } from '@/services';
import type {
  ComplianceEngineResponse,
  ComplianceLineItem,
} from '@/services';
import { ComplianceLineItemDetail, formatComplianceCurrency } from './ComplianceLineItemDetail';
import { AIComplianceSummaryBar } from './AIComplianceSummaryBar';

interface AIComplianceTabProps {
  invoiceId: string;
  priceBookId: string | null;
}

const statusConfig = {
  PASS: { label: 'Pass', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle2 },
  FAIL: { label: 'Fail', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: AlertTriangle },
  FLAG: { label: 'Flag', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: AlertCircle },
};

// formatCurrency alias for local use — delegates to shared helper
const formatCurrency = formatComplianceCurrency;

export default function AIComplianceTab({ invoiceId }: AIComplianceTabProps) {
  const { toast } = useToast();
  const [response, setResponse] = useState<ComplianceEngineResponse | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [showRawJson, setShowRawJson] = useState(false);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  // Load the latest saved run on mount
  const { data: savedRun } = useQuery({
    queryKey: ['compliance-engine-run', invoiceId],
    queryFn: () => complianceEngineService.latestByInvoice(invoiceId),
    enabled: !!invoiceId,
  });

  // Hydrate response from saved run if no fresh run has been performed
  useEffect(() => {
    if (savedRun && !response) {
      setResponse({
        success: true,
        results: savedRun.results ?? undefined,
        metadata: savedRun.metadata ?? undefined,
      });
    }
  }, [savedRun, response]);

  const runEngine = useMutation({
    mutationFn: () => complianceEngineService.run(invoiceId),
    onSuccess: async (data) => {
      setResponse(data);
      setExpandedRows(new Set());
      if (!data.success) {
        toast({ title: 'Error', description: data.error || 'Compliance engine failed', variant: 'destructive' });
      }
      // Server persists the run as part of complianceEngineService.run();
      // no separate save step required.
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const toggleRow = (lineNumber: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(lineNumber)) next.delete(lineNumber);
      else next.add(lineNumber);
      return next;
    });
  };

  const results = response?.results;
  const metadata = response?.metadata;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button onClick={() => runEngine.mutate()} disabled={runEngine.isPending}>
          {runEngine.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <ShieldCheck className="w-4 h-4 mr-2" />
          )}
          {runEngine.isPending ? 'Analyzing...' : 'Run Full Review'}
        </Button>

        {metadata && (
          <div className="text-xs text-muted-foreground">
            Model Response Time: {(metadata.processing_time_ms / 1000).toFixed(1)}s
          </div>
        )}
      </div>

      {/* Running state */}
      {runEngine.isPending && (
        <div className="p-8 text-center bg-card border border-border rounded-lg">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Analyzing invoice against price book and field ticket...
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            This may take 30-60 seconds.
          </p>
        </div>
      )}

      {/* Empty state */}
      {!response && !runEngine.isPending && (
        <div className="p-8 text-center text-muted-foreground bg-card border border-border rounded-lg">
          Run Full Review to analyze this invoice against the price book and field ticket.
        </div>
      )}

      {/* Error state (no results) */}
      {response && !response.success && !results && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-400">
          {response.error || 'Unknown error'}
        </div>
      )}

      {/* Results */}
      {results && (
        <>
          {/* Compliance summary bar */}
          <AIComplianceSummaryBar metadata={metadata} lineItems={results.line_items} />

          {/* Line items table */}
          {results.line_items.length > 0 && (
            <LineItemsTable
              items={results.line_items}
              expandedRows={expandedRows}
              onToggle={toggleRow}
            />
          )}

          {/* Missing charges */}
          {results.missing_charges && results.missing_charges.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Missing Charges</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {results.missing_charges.map((mc, i) => (
                  <div key={i} className="bg-card border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{mc.item_code}</span>
                      {mc.estimated_amount != null && (
                        <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                          {formatCurrency(mc.estimated_amount)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{mc.description}</p>
                    <p className="text-xs text-muted-foreground mt-1 italic">{mc.expected_reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* General observations */}
          {results.general_observations && results.general_observations.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">General Observations</h3>
              <div className="bg-card border border-border rounded-lg p-4">
                <ul className="list-disc list-inside space-y-1">
                  {results.general_observations.map((obs, i) => (
                    <li key={i} className="text-sm text-muted-foreground">
                      {typeof obs === 'string' ? obs : (
                        <span>
                          {(obs as Record<string, unknown>).description
                            ? String((obs as Record<string, unknown>).description)
                            : JSON.stringify(obs)}
                          {(obs as Record<string, unknown>).recommendation && (
                            <span className="italic text-xs ml-1">
                              — {String((obs as Record<string, unknown>).recommendation)}
                            </span>
                          )}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Raw JSON Response */}
          <Collapsible open={showRawJson} onOpenChange={setShowRawJson}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                <span className="text-muted-foreground">Raw JSON Response</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showRawJson ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Card className="mt-2">
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
                    Model: {metadata?.model}
                    {metadata?.system_prompt && (
                      <button
                        className="text-primary hover:underline"
                        onClick={() => setShowSystemPrompt(true)}
                      >
                        View System Prompt
                      </button>
                    )}
                  </div>
                  <pre className="text-xs overflow-auto max-h-96 bg-muted p-4 rounded-lg">
                    {JSON.stringify(response, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}

      <Dialog open={showSystemPrompt} onOpenChange={setShowSystemPrompt}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>System Prompt</DialogTitle>
            <DialogDescription>
              Prompt sent to {metadata?.model} (source: {metadata?.system_prompt_source})
            </DialogDescription>
          </DialogHeader>
          <pre className="text-xs overflow-auto max-h-[60vh] bg-muted p-4 rounded-lg whitespace-pre-wrap">
            {metadata?.system_prompt}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Sub-components ---

function LineItemsTable({
  items,
  expandedRows,
  onToggle,
}: {
  items: ComplianceLineItem[];
  expandedRows: Set<number>;
  onToggle: (lineNumber: number) => void;
}) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="px-3 py-2 text-left w-8"></th>
            <th className="px-3 py-2 text-left">Line</th>
            <th className="px-3 py-2 text-left">Item Code</th>
            <th className="px-3 py-2 text-right">Qty</th>
            <th className="px-3 py-2 text-left">UOM</th>
            <th className="px-3 py-2 text-left">Description</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Notes</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const isExpanded = expandedRows.has(i);
            const config = statusConfig[item.status] || statusConfig.FLAG;
            const StatusIcon = config.icon;
            const hasDetails = item.types != null || item.total_impact != null || Object.keys(item.checks).length > 0;

            return (
              <LineItemRow
                key={i}
                item={item}
                isExpanded={isExpanded}
                hasDetails={hasDetails}
                config={config}
                StatusIcon={StatusIcon}
                onToggle={() => onToggle(i)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LineItemRow({
  item,
  isExpanded,
  hasDetails,
  config,
  StatusIcon,
  onToggle,
}: {
  item: ComplianceLineItem;
  isExpanded: boolean;
  hasDetails: boolean;
  config: { label: string; className: string };
  StatusIcon: typeof CheckCircle2;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={`border-b border-border hover:bg-muted/30 ${hasDetails ? 'cursor-pointer' : ''}`}
        onClick={hasDetails ? onToggle : undefined}
      >
        <td className="px-3 py-2">
          {hasDetails && (
            isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </td>
        <td className="px-3 py-2 font-mono">{item.line_number}</td>
        <td className="px-3 py-2 font-mono">{item.item_code}</td>
        <td className="px-3 py-2 text-right font-mono">{item.quantity != null ? Math.round(item.quantity) : '—'}</td>
        <td className="px-3 py-2">{item.uom ?? '—'}</td>
        <td className="px-3 py-2 max-w-[200px] truncate" title={item.description}>{item.description}</td>
        <td className="px-3 py-2 text-right font-mono">{formatCurrency(item.invoiced_amount)}</td>
        <td className="px-3 py-2">
          <Badge className={config.className}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        </td>
        <td className="px-3 py-2 text-muted-foreground max-w-[250px] truncate" title={item.notes}>{item.notes}</td>
      </tr>
      {isExpanded && (
        <tr className="bg-muted/20">
          <td colSpan={9} className="px-6 py-3">
            <ComplianceLineItemDetail item={item} />
          </td>
        </tr>
      )}
    </>
  );
}

